'use client';

import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import SplashButton from './SplashButton';
import { sendGTMEvent } from '@next/third-parties/google';
import { usePostHog } from 'posthog-js/react';
import { POSTHOG_EVENTS } from '@/lib/posthog';

interface AppModalProps {
  appUrl: string;
  buttonText?: string;
  modalTitle?: string;
  appAnalyticsId?: string;
}

export default function AppModal({ appUrl, buttonText, modalTitle = 'App Modal', appAnalyticsId }: AppModalProps) {
  const [isOpen, setIsOpen] = useState(false);
  const posthog = usePostHog();
  const calculatorType = appAnalyticsId || 'netsuite_pricing';

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      console.log('log ryan event', event);

      // Check if the message indicates calculator completion
      if (event.data?.eventName === 'calculator_completion') {
        sendGTMEvent({
          event: 'pricing_calculator_complete',
          category: 'Calculator',
          action: 'Complete',
          label: 'NetSuite Pricing Calculator',
          value: 1,
          event_id: new Date().getTime().toString(),
          transport_url: undefined,
          non_interaction: false,
        });

        // PostHog event (for product analytics)
        if (posthog) {
          posthog.capture(POSTHOG_EVENTS.CALCULATOR_COMPLETED, {
            calculator_type: calculatorType,
            completion_timestamp: new Date().toISOString(),
            ...(event.data?.additionalData || {}),
          });
          console.log('[PostHog] Calculator completion tracked');
        }

        // GTM event matching PostHog event
        sendGTMEvent({
          event: POSTHOG_EVENTS.CALCULATOR_COMPLETED,
          calculator_type: calculatorType,
          completion_timestamp: new Date().toISOString(),
          ...(event.data?.additionalData || {}),
        });

        // LinkedIn conversion tracking
        if (typeof window !== 'undefined' && typeof (window as any).lintrk !== 'undefined') {
          (window as any).lintrk('track', { conversion_id: 23651404 });
          console.log('[LinkedIn] Calculator completed conversion tracked');
        }
      }

      // Check if the message indicates calculator start
      if (event.data?.eventName === 'survey_start') {
        console.log('survey_start sending');
        sendGTMEvent({
          event: 'pricing_calculator_start',
          category: 'Calculator',
          action: 'Start',
          label: 'NetSuite Pricing Calculator',
          value: 1,
          event_id: new Date().getTime().toString(),
          transport_url: undefined,
          non_interaction: false,
        });

        // PostHog event
        if (posthog) {
          posthog.capture(POSTHOG_EVENTS.CALCULATOR_STARTED, {
            calculator_type: calculatorType,
            start_timestamp: new Date().toISOString(),
            modal_opened: isOpen,
          });
          console.log('[PostHog] Calculator start tracked');
        }

        // GTM event matching PostHog event
        sendGTMEvent({
          event: POSTHOG_EVENTS.CALCULATOR_STARTED,
          calculator_type: calculatorType,
          start_timestamp: new Date().toISOString(),
          modal_opened: isOpen,
        });

        // LinkedIn conversion tracking
        if (typeof window !== 'undefined' && typeof (window as any).lintrk !== 'undefined') {
          (window as any).lintrk('track', { conversion_id: 23651396 });
          console.log('[LinkedIn] Calculator started conversion tracked');
        }
      }

      // Check if the message indicates email reached (checkout)
      if (event.data?.eventName === 'email_reached') {
        console.log('email_reached - calculator checkout');

        // PostHog event - High value conversion point!
        if (posthog) {
          posthog.capture(POSTHOG_EVENTS.CALCULATOR_CHECKOUT, {
            calculator_type: calculatorType,
            checkout_timestamp: new Date().toISOString(),
            ...(event.data?.additionalData || {}),
          });
          console.log('[PostHog] Calculator checkout tracked');
        }

        // GTM event for marketing analytics
        sendGTMEvent({
          event: 'pricing_calculator_checkout',
          category: 'Calculator',
          action: 'Checkout',
          label: 'NetSuite Pricing Calculator - Email Reached',
          value: 1,
          event_id: new Date().getTime().toString(),
          transport_url: undefined,
          non_interaction: false,
        });

        // GTM event matching PostHog event
        sendGTMEvent({
          event: POSTHOG_EVENTS.CALCULATOR_CHECKOUT,
          calculator_type: calculatorType,
          checkout_timestamp: new Date().toISOString(),
          ...(event.data?.additionalData || {}),
        });
      }
    };

    window.addEventListener('message', handleMessage);

    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [posthog, isOpen, calculatorType]);

  // Track when modal opens
  const handleOpenModal = () => {
    setIsOpen(true);
    if (posthog) {
      posthog.capture(POSTHOG_EVENTS.CALCULATOR_MODAL_OPENED, {
        calculator_type: calculatorType,
        trigger: 'button_click',
        timestamp: new Date().toISOString(),
      });
      console.log('[PostHog] Calculator modal opened');
    }

    // GTM event matching PostHog event
    sendGTMEvent({
      event: POSTHOG_EVENTS.CALCULATOR_MODAL_OPENED,
      calculator_type: calculatorType,
      trigger: 'button_click',
      timestamp: new Date().toISOString(),
    });
  };

  return (
    <>
      <SplashButton
        as="button"
        text={buttonText || 'Get My NetSuite Estimate'}
        onClick={handleOpenModal}
        size="md"
        icon="start"
        className="min-w-[280px] sm:min-w-[380px]"
      />
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent
          className="max-w-screen m-0 h-[100dvh] max-h-[100dvh] w-screen gap-0 overflow-hidden rounded-none border-0 p-0 sm:h-[95vh] sm:max-h-[95vh] sm:w-[95vw] sm:max-w-[95vw] sm:rounded-lg sm:border sm:border-white/30 [&>button>svg]:h-8 [&>button>svg]:w-8"
          closeButtonColor="text-white"
        >
          <DialogTitle className="sr-only">{modalTitle}</DialogTitle>
          <iframe
            src={appUrl}
            className="h-full w-full border-0"
            title={modalTitle}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        </DialogContent>
      </Dialog>
    </>
  );
}
